import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styles from "./BlogDetailsPage.module.css";
import { blogService } from "../../services/blog.service";
import { getFontCss, getSecondaryKeywords, parseMediaUrl } from "../../utils/blogHelpers";
import { highlightGoldCoast } from "../../utils/highlightGoldCoast";

const MediaFrame = ({ src, url, caption, alt }) => {
  const parsed = parseMediaUrl(url || "");

  return (
    <figure className={styles.inlineFigure}>
      {src ? (
        src.startsWith("data:video") || src.endsWith(".mp4") || src.endsWith(".webm") ? (
          <video className={styles.inlineVideo} src={src} controls />
        ) : (
          <img src={src} alt={alt || caption || "Blog media"} />
        )
      ) : null}

      {!src && parsed?.embedSrc ? (
        <div className={styles.embedWrap}>
          <iframe
            src={parsed.embedSrc}
            title={caption || alt || "Embedded video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : null}

      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
};

const SourceLink = ({ url, label }) => {
  const parsed = parseMediaUrl(url);
  if (!parsed) return null;

  if (parsed.embedSrc) {
    return (
      <figure className={styles.inlineFigure}>
        <div className={styles.embedWrap}>
          <iframe
            src={parsed.embedSrc}
            title={label || "Embedded source"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {label ? <figcaption>{label}</figcaption> : null}
      </figure>
    );
  }

  return (
    <a className={styles.sourceCard} href={parsed.href} target="_blank" rel="noreferrer">
      <span>Source link</span>
      <strong>{label || parsed.href}</strong>
    </a>
  );
};

const BlogDetailsPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(undefined);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      blogService.getBlogBySlug(slug),
      blogService.getBlogs(),
    ]).then(([current, allBlogs]) => {
      if (!isMounted) return;
      setBlog(current);
      setRelatedBlogs(
        allBlogs.filter((item) => item.slug !== slug).slice(0, 3)
      );
    });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (blog === undefined) {
    return (
      <div className={styles.notFoundPage}>
        <h1>Loading story...</h1>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className={styles.notFoundPage}>
        <h1>Blog Not Found</h1>
        <p>The blog you are looking for does not exist.</p>
        <Link to="/blogs">Back to Blogs</Link>
      </div>
    );
  }

  const secondaryKeywords = getSecondaryKeywords(blog);

  return (
    <div className={styles.detailsPage}>
      <section className={styles.detailsHero}>
        <div className={styles.container}>
          <Link to="/blogs" className={styles.backLink}>
            ← Back to Blogs
          </Link>

          <div className={styles.metaRow}>
            <span>{blog.category}</span>
            <span>{blog.date}</span>
            <span>{blog.readTime}</span>
          </div>

          <h1>{blog.title}</h1>
          <p>{blog.shortDescription}</p>

          {secondaryKeywords.length ? (
            <div className={styles.pillGroup}>
              <span className={styles.pillLabel}>Secondary keywords</span>
              <div className={styles.tagRow}>
                {secondaryKeywords.map((keyword) => (
                  <span className={styles.keywordPill} key={keyword}>
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {blog.tags?.length ? (
            <div className={styles.pillGroup}>
              <span className={styles.pillLabel}>Tags</span>
              <div className={styles.tagRow}>
                {blog.tags.map((tag) => (
                  <span className={styles.tagPill} key={tag}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <section className={styles.articleSection}>
        <div className={styles.container}>
          <div className={styles.articleLayout}>
            <article className={styles.articleCard}>
              <div className={styles.articleImage}>
                {blog.bannerImage || blog.image ? (
                  <img src={blog.bannerImage || blog.image} alt={blog.title} />
                ) : null}
              </div>

              <div className={styles.articleContent}>
                {blog.content.map((section, index) => {
                  if (section.type === "image") {
                    return (
                      <MediaFrame
                        key={`${section.src}-${index}`}
                        src={section.src}
                        caption={section.caption}
                        alt={section.alt || blog.title}
                      />
                    );
                  }

                  if (section.type === "video") {
                    return (
                      <MediaFrame
                        key={`video-${index}`}
                        src={section.src}
                        url={section.url}
                        caption={section.caption}
                        alt={blog.title}
                      />
                    );
                  }

                  if (section.type === "embed") {
                    return (
                      <SourceLink
                        key={`embed-${index}`}
                        url={section.url}
                        label={section.label}
                      />
                    );
                  }

                  const headingStyle = {
                    fontFamily: section.headingFontFamily,
                    fontSize: section.headingFontSize,
                    ...getFontCss(section.headingFontStyle || "bold"),
                  };
                  const contentStyle = {
                    fontFamily: section.contentFontFamily,
                    fontSize: section.contentFontSize,
                    ...getFontCss(section.contentFontStyle || "normal"),
                  };

                  return (
                    <div className={styles.contentBlock} key={`${section.heading}-${index}`}>
                      {section.heading ? <h2 style={headingStyle}>{section.heading}</h2> : null}

                      {(section.paragraphs || []).map((paragraph, pIndex) => (
                        <p key={pIndex} style={contentStyle}>
                          {paragraph}
                        </p>
                      ))}

                      {section.image ? (
                        <MediaFrame src={section.image} alt={section.imageAlt || section.heading} />
                      ) : null}

                      {section.video || section.videoUrl ? (
                        <MediaFrame
                          src={section.video}
                          url={section.videoUrl}
                          caption={section.videoCaption}
                          alt={section.heading}
                        />
                      ) : null}

                      {section.sourceUrl ? (
                        <SourceLink url={section.sourceUrl} label={section.sourceLabel} />
                      ) : null}
                    </div>
                  );
                })}

                <div className={styles.finalCta}>
                  <h3>Looking for comfortable accommodation?</h3>
                  <p>
                    {highlightGoldCoast(
                      "Choose Arabella Motor Inn for comfortable accommodation in Tweed Heads, with a relaxing Tweed Heads South, NSW stay and convenient access to nearby destinations. Rooms include air conditioning, tea and coffee facilities, a bar fridge, and free Wi-Fi."
                    )}
                  </p>

                  <Link to="/contact" className={styles.contactButton}>
                    Contact Us
                  </Link>
                </div>
              </div>
            </article>

            <aside className={styles.sidebar}>
              {(blog.primaryKeyword || secondaryKeywords.length || blog.tags?.length) ? (
                <div className={styles.sideCard}>
                  <h3>Keywords & Tags</h3>
                  {blog.primaryKeyword ? (
                    <div className={styles.sidePillBlock}>
                      <span className={styles.pillLabel}>Primary</span>
                      <div className={styles.sideTags}>
                        <span className={styles.primaryPill}>{blog.primaryKeyword}</span>
                      </div>
                    </div>
                  ) : null}
                  {secondaryKeywords.length ? (
                    <div className={styles.sidePillBlock}>
                      <span className={styles.pillLabel}>Secondary</span>
                      <div className={styles.sideTags}>
                        {secondaryKeywords.map((keyword) => (
                          <span className={styles.keywordPill} key={keyword}>
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {blog.tags?.length ? (
                    <div className={styles.sidePillBlock}>
                      <span className={styles.pillLabel}>Tags</span>
                      <div className={styles.sideTags}>
                        {blog.tags.map((tag) => (
                          <span className={styles.tagPill} key={tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className={styles.sideCard}>
                <h3>Recent Blogs</h3>

                {relatedBlogs.map((item) => (
                  <Link
                    to={`/blogs/${item.slug}`}
                    className={styles.relatedBlog}
                    key={item.id}
                  >
                    <span>{item.category}</span>
                    <h4>{item.title}</h4>
                    <p>{item.readTime}</p>
                  </Link>
                ))}
              </div>

              <div className={styles.sideCard}>
                <h3>Quick Links</h3>
                <Link to="/">Home</Link>
                <Link to="/about">About Us</Link>
                <Link to="/gallery">Gallery</Link>
                <Link to="/blogs">Blogs</Link>
                <Link to="/contact">Contact Us</Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetailsPage;
